import React from 'react';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
} from '@billda/design-system';
import { RefreshIcon, CancelIcon } from '@billda/design-system';
import { PendingInviteDto } from '../../api/userManagementApi';

interface PendingInvitesListProps {
  invites: PendingInviteDto[];
  isLoading: boolean;
  onResend: (invite: PendingInviteDto) => void;
  onRevoke: (invite: PendingInviteDto) => void;
  isResending: boolean;
  isRevoking: boolean;
}

const formatTimeRemaining = (expiresAt: string): string => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'Expired';
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `Expires in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }

  if (diffHours > 0) {
    return `Expires in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  return `Expires in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const PendingInvitesList: React.FC<PendingInvitesListProps> = ({
  invites,
  isLoading,
  onResend,
  onRevoke,
  isResending,
  isRevoking,
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="pt-4">
              <div className="flex flex-col gap-1.5">
                <Skeleton width="70%" height={24} />
                <Skeleton width="50%" height={20} />
                <Skeleton width="60%" height={20} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (invites.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="type-body-md text-content-secondary">
          No pending invitations
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {invites.map(invite => {
        const isExpired = invite.isExpired;
        const canRevoke = !isExpired;

        return (
          <Card
            key={invite.invitationId}
            data-testid={`invite-card-${invite.invitationId}`}
          >
            <CardContent className="pt-4">
              <div className="flex flex-col gap-4">
                {/* Header: Email and Status */}
                <div className="flex items-start justify-between">
                  <p className="type-body-lg font-medium">
                    {invite.invitedEmail}
                  </p>
                  <div className="flex gap-0.5">
                    <Chip
                      label={
                        isExpired
                          ? 'Expired'
                          : formatTimeRemaining(invite.expiresAt)
                      }
                      size="sm"
                      color={isExpired ? 'error' : 'default'}
                      variant={isExpired ? 'filled' : 'outlined'}
                    />
                    {invite.hasBeenAccessed && !isExpired && (
                      <Chip
                        label="Accessed"
                        size="sm"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between">
                    <span className="type-body-md text-content-secondary">
                      Invited by
                    </span>
                    <span className="type-body-md font-medium">
                      {invite.invitedByUserName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="type-body-md text-content-secondary">
                      Sent on
                    </span>
                    <span className="type-body-md font-medium">
                      {formatDate(invite.createdAt)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onResend(invite)}
                    disabled={isResending || isRevoking}
                    data-testid={`resend-invite-${invite.invitationId}`}
                  >
                    <RefreshIcon aria-hidden="true" className="size-4" />
                    Resend
                  </Button>
                  {canRevoke && (
                    <Button
                      size="sm"
                      variant="outlineDanger"
                      onClick={() => onRevoke(invite)}
                      disabled={isResending || isRevoking}
                      data-testid={`revoke-invite-${invite.invitationId}`}
                    >
                      <CancelIcon aria-hidden="true" className="size-4" />
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
